import { User } from "@/api/User"
import { WorkModelEntry } from "@/api/WorkModelEntry";
import { DialogBody, DialogCloseTrigger, DialogContent, DialogRoot } from "@/components/ui/dialog";
import { formatOverview, localTimezone } from "@/data/DateTimeUtil";
import IdAndNameData from "@/data/IdAndNameData";
import { workmodelService } from "@/service/WorkModelService";
import { DataList, Text, Flex, useDisclosure, IconButton } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { ContractForm } from "../timesheet/ContractForm";
import type { ContactFormData } from "../timesheet/ContractForm";
import AddButton from "@/components/buttons/AddButton";
import { Tooltip } from "@/components/ui/tooltip";
import { LuPencilLine, LuTrash } from "react-icons/lu";
import ConfirmDialog from "@/components/ConfirmDialog";
import WaitingSpinner from "@/components/WaitingSpinner";
import { NamingUtil } from "@/data/NamingUtil";


type WorkModelEntrySectionProps = {
    user: User;
    canEdit?: boolean;
};

const WorkModelEntrySection = ({ user, canEdit = true }: WorkModelEntrySectionProps) => {

    const [isLoading, setIsLoading] = useState(false);
    const [workmodelEntries, setWorkmodelEntries] = useState<WorkModelEntry[]>([]);
    const { open: isContractOpen, onOpen: onContractOpen, onClose: onContractClose } = useDisclosure();
    const { open: isDeleteContractOpen, onOpen: onDeleteContractOpen, onClose: onDeleteContractClose } = useDisclosure();
    const [editData, setEditData] = useState<WorkModelEntry | null>(null);
    const [deleteData, setDeleteData] = useState<IdAndNameData<number> | null>(null);

    useEffect(() => {
        setIsLoading(true);
        if (user) {
            workmodelService.findAll(user?.id).then(r => setWorkmodelEntries(r)).finally(() => setIsLoading(false));
        }
    }, [user]);

    const newContract = () => {
        setEditData(null);
        onContractOpen();
    }

    const saveContract = (c: ContactFormData) => {
        console.log(c);
        let wm: WorkModelEntry = {
            id: editData?.id ?? -1,
            userId: user?.id,
            localValidFrom: c.interval.start,
            timezone: localTimezone,
            model: c.model,
            organizationId: user?.organizationId
        };
        editData ?
            workmodelService.update(wm).then(id => {
                const idx = workmodelEntries.findIndex(e => e.id === id);
                if (idx >= 0) {
                    workmodelEntries[idx] = wm;
                    setWorkmodelEntries([...workmodelEntries]);
                }
            }) :
            workmodelService.add(wm).then(id => {
                wm.id = id;
                setWorkmodelEntries([...workmodelEntries, wm]);
            });
        setEditData(null);
        onContractClose();
    }

    const deleteContract = (id: number) => {
        workmodelService.remove(user?.id, id).then(() => {
            const idx = workmodelEntries.findIndex(e => e.id === id);
            if (idx >= 0) {
                workmodelEntries.splice(idx, 1);
                setWorkmodelEntries([...workmodelEntries]);
            }
        });
        setDeleteData(null);
        onDeleteContractClose();
    }

    const ContractDialog = () => {
        return (
            <DialogRoot open={isContractOpen} onOpenChange={() => onContractClose()}
                placement='center'
                size='sm'
                motionPreset='scale'
                scrollBehavior='outside'
            >
                <DialogContent>
                    <DialogCloseTrigger />
                    <DialogBody>
                        <ContractForm
                            showInterval={false}
                            editContract={editData && {
                                interval: {
                                    start: editData.localValidFrom,
                                    end: editData.localValidFrom
                                },
                                model: editData.model
                            }}
                            submitCaption='Speichern' onContractSubmit={saveContract}
                            cancelCaption='Abbrechen' onContractCancel={onContractClose} />
                    </DialogBody>
                </DialogContent>
            </DialogRoot>
        );
    }


    return (
        <>
            {canEdit && <AddButton caption='Neues Anstellungsverhältnis' onClick={newContract} />}
            {isLoading && <WaitingSpinner size='sm' />}
            {workmodelEntries.length === 0 ?
                <Text textStyle='sm'>keine Einträge</Text> :
                <DataList.Root orientation={{ base: 'vertical', sm: 'horizontal' }} gap={{ base: '4', sm: '0' }}>
                    {workmodelEntries.map((entry: WorkModelEntry) => {
                        const name = NamingUtil.contract(entry.model);
                        const from = formatOverview(entry.localValidFrom);
                        return (
                            <DataList.Item key={entry.id} flexDirection={{ sm: 'row' }}>
                                <DataList.ItemLabel>{from}</DataList.ItemLabel>
                                <DataList.ItemValue>{name}</DataList.ItemValue>
                                {canEdit && (
                                    <Flex>
                                        <Tooltip content='Bearbeiten'>
                                            <IconButton aria-label="Bearbeiten" colorPalette="blue" variant="ghost" size="xs" onClick={() => {
                                                setEditData(entry);
                                                onContractOpen();
                                            }}>
                                                <LuPencilLine />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip content='Löschen'>
                                            <IconButton aria-label="Löschen" colorPalette="red" variant="ghost" size="xs" onClick={() => {
                                                let da: IdAndNameData<number> = {
                                                    id: entry.id,
                                                    name: `${name} ab ${from}`
                                                };
                                                setDeleteData(da);
                                                onDeleteContractOpen();
                                            }}>
                                                <LuTrash />
                                            </IconButton>
                                        </Tooltip>
                                    </Flex>
                                )}
                            </DataList.Item>
                        );
                    })}
                </DataList.Root>}
            {canEdit && (
                <>
                    <ContractDialog />
                    <ConfirmDialog
                        heading={`${deleteData?.name} Löschen`}
                        isOpen={isDeleteContractOpen}
                        onClose={onDeleteContractClose}
                        onConfirm={async (): Promise<void> => deleteContract(deleteData.id)}
                    />
                </>
            )}
        </>
    )
}

export default WorkModelEntrySection;
